import { useEffect, useState } from 'react';
import { subscribeSyncState, SyncAPI } from '@/lib/firebaseSync';
import { WifiOff, CloudOff, Cloud, AlertTriangle, RefreshCcw } from 'lucide-react';
import { toast } from 'sonner';

/**
 * Badge pequeno que reflete o estado do sync (Firebase RTDB).
 * - Verde + Cloud  : conectado + autenticado → sync ativo
 * - Vermelho + Alert: conectado MAS auth falhou (regras/anonymous) → writes negados
 * - Âmbar + WifiOff : inicializado, offline no momento
 * - Slate + CloudOff: sync desativado (100% local)
 *
 * Quando há authError, clicar tenta re-autenticar via SyncAPI.retryAuth().
 */
export default function SyncStatusBadge({ compact = false }) {
  const [state, setState] = useState({ connected: false, initialized: false, authenticated: false, authError: null });
  const [retrying, setRetrying] = useState(false);

  useEffect(() => subscribeSyncState(setState), []);

  const hasAuthError = state.initialized && state.connected && !state.authenticated;

  let color, Icon, label, title;
  if (!state.initialized) {
    color = 'bg-slate-100 text-slate-500 border-slate-200';
    Icon = CloudOff;
    label = 'Offline Local';
    title = 'Firebase não configurado — modo offline local. Ative em /config.';
  } else if (hasAuthError) {
    color = 'bg-rose-50 text-rose-700 border-rose-300 cursor-pointer hover:bg-rose-100';
    Icon = AlertTriangle;
    label = 'Auth Bloqueada';
    title = state.authError?.message
      || 'Conectado ao Firebase mas autenticação falhou — writes serão recusados. Clique pra tentar de novo.';
  } else if (state.connected) {
    color = 'bg-emerald-50 text-emerald-700 border-emerald-200 animate-in fade-in';
    Icon = Cloud;
    label = 'Cloud Connected';
    title = 'Conectado ao Firebase Realtime DB — sync multi-device ativo';
  } else {
    color = 'bg-amber-50 text-amber-700 border-amber-200';
    Icon = WifiOff;
    label = 'Sem internet';
    title = 'Firebase ativado mas sem conexão — mudanças serão enviadas quando voltar';
  }

  const handleRetry = async () => {
    if (!hasAuthError || retrying) return;
    setRetrying(true);
    const r = await SyncAPI.retryAuth();
    setRetrying(false);
    if (r.ok) {
      toast.success('Autenticado! Sync ativo.');
    } else {
      toast.error('Auth bloqueada', {
        description: r.error?.message || 'Verifique Firebase Console → Authentication → Anonymous.',
        duration: 8000,
      });
    }
  };

  return (
    <div
      data-testid="sync-status-badge"
      onClick={hasAuthError ? handleRetry : undefined}
      role={hasAuthError ? 'button' : undefined}
      className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-full border text-[10px] font-bold ${color}`}
      title={title}
    >
      {retrying ? <RefreshCcw size={10} className="animate-spin" strokeWidth={2.5} /> : <Icon size={10} strokeWidth={2.5} />}
      {compact ? null : (retrying ? 'Re-autenticando...' : label)}
    </div>
  );
}
